"use client";

import { Bot, MoreHorizontal, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { PermissionGate } from "@/components/shared/permission-gate";
import { cn } from "@/lib/utils";
import { CommandAgent } from "@/types/command-agent";

interface CommandAgentCardProps {
    agent: CommandAgent;
    onOpen: () => void;
    onToggleActive: () => void;
    onDelete: () => void;
    isTogglePending?: boolean;
    showActiveToggle?: boolean;
}

export function CommandAgentCard({
    agent,
    onOpen,
    onToggleActive,
    onDelete,
    isTogglePending = false,
    showActiveToggle = true,
}: CommandAgentCardProps) {
    const isActive = Boolean(agent.is_active);

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen();
                }
            }}
            className="group flex cursor-pointer flex-col gap-3 rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition-all hover:border-primary/40 hover:shadow-md"
        >
            <div className="flex items-start gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-[9px] bg-violet-100 dark:bg-violet-900/30">
                    <Bot className="size-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold sm:text-[15px]">{agent.name}</p>
                    {agent.role ? (
                        <Badge
                            variant="outline"
                            className="mt-1 rounded-full border-violet-200 bg-violet-50 text-[11px] font-medium text-primary dark:border-violet-900/60 dark:bg-violet-950/40"
                        >
                            {agent.role}
                        </Badge>
                    ) : null}
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            onClick={(event) => event.stopPropagation()}
                            className="size-7 shrink-0 p-0 opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
                        >
                            <MoreHorizontal className="size-4 text-muted-foreground" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                        <DropdownMenuItem onClick={onOpen}>Edit</DropdownMenuItem>
                        <PermissionGate requiredPermissions={["DELETE_COMMAND_AGENT"]}>
                            <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={onDelete}
                            >
                                <Trash2 className="mr-2 size-3.5" />
                                Delete
                            </DropdownMenuItem>
                        </PermissionGate>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <p className="line-clamp-2 min-h-10 text-[13px] leading-relaxed text-muted-foreground">
                {agent.description || "No description yet."}
            </p>

            <div
                className="flex items-center justify-between border-t border-border/60 pt-3"
                onClick={(event) => event.stopPropagation()}
            >
                <span
                    className={cn(
                        "inline-flex items-center gap-1.5 text-xs font-medium",
                        isActive
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-muted-foreground"
                    )}
                >
                    <span
                        className={cn(
                            "size-1.5 rounded-full",
                            isActive ? "bg-emerald-500" : "bg-muted-foreground/40"
                        )}
                    />
                    {isActive ? "Active" : "Paused"}
                </span>
                {showActiveToggle ? (
                    <PermissionGate requiredPermissions={["UPDATE_COMMAND_AGENT"]}>
                        <Switch
                            size="sm"
                            checked={isActive}
                            disabled={isTogglePending}
                            onCheckedChange={onToggleActive}
                            aria-label={isActive ? "Pause agent" : "Activate agent"}
                        />
                    </PermissionGate>
                ) : null}
            </div>
        </div>
    );
}
